import type { EngraveWords, OrderWords } from '../ui/words';
import { formatIdr, isSunsetId, SIGNATURE_SUNSET_SUPPLEMENT_IDR } from '../config/appearance';

/**
 * A110V « Je veux cette paire » — pure helpers (no DOM): the spec lines shown
 * in the panel, the WhatsApp message and link, the Shopify cart payload and
 * variant choice, the product URL and the layout maths of the try-on photo.
 */

/** Maison Mata WhatsApp, international format without « + » (wa.me). */
export const WHATSAPP_NUMBER = '6281238660613';
/** Product pages when the try-on is not embedded on Shopify (standalone catalogue). */
export const PUBLIC_SHOP_ROOT = 'https://maisonmata.com/';
/** Light / dark lens intensities of the Colours panel (any other value is not worded). */
export const INTENSITY_LIGHT = 30;
export const INTENSITY_DARK = 85;

/** The try-on photo: 4:5 portrait, square photo on top, information band below. */
export const ORDER_PHOTO = {
  width: 1080,
  height: 1350,
  photo: 1080,
  band: 270,
  quality: 0.9,
  paper: '#f6f2ec',
  ink: '#1d1d1b',
  /** Muted ink for the product address (5.7:1 on the paper). */
  muted: '#5b5750',
  margin: 72,
} as const;

/** Default crop focus (fractions of the snapshot): centred, slightly above the middle. */
export const DEFAULT_PHOTO_FOCUS = { x: 0.5, y: 0.42 } as const;

export interface OrderMaterials {
  /** `original` or an acetate library id. */
  acetateId: string;
  /** Display name of the acetate; null for `original`. */
  acetateName: string | null;
  /** Supplier reference (« J810907-6 ») when known. */
  acetateRef: string | null;
  /** `original`, a classic tint id or a Sunset lens id. */
  lensId: string;
  /** Display name of the lens; null for `original`. */
  lensName: string | null;
  /** Lens density 0–100 (30 = light, 85 = dark). */
  density: number;
  gradient: boolean;
  /** Gradient state of the frame's own lens (the « Original » setting). */
  defaultGradient: boolean;
  /**
   * A200: Signature frame — its acetate is the product's own (no acetate line) and its lens is
   * « Original », a classic tint or « Lunettes de vue », named alone (no intensity, no gradient).
   */
  lensOnly?: boolean;
}

/**
 * A110Z — the temple engraving when it differs from the house marks: font and colour names, the
 * number of customer texts, and each changed slot (its label, text — empty for an emptied slot —
 * and the size / position detail when the customer moved away from the automatic placement).
 */
export interface EngravingOrder {
  fontName: string;
  inkName: string;
  texts: number;
  /** A201: the four views, always — the logo first (its text is « Logo Maison Mata »), then the three texts. */
  slots: Array<{ slot: 'il' | 'ir' | 'el' | 'er'; label: string; text: string; detail: string | null }>;
}

/** Everything the customer chose, read from the try-on at the moment of the action. */
export interface OrderSnapshot {
  frameId: string;
  model: string;
  colorway: string;
  handle: string;
  /** Null when the frame has no material preview (fixed colourway). */
  materials: OrderMaterials | null;
  /** Size letter as the Size panel shows it (Handmade) and total front width. */
  size: { label: string | null; widthMm: number | null };
  /** PD as the Size panel shows it: from the head size (estimated) or typed in. */
  pd: { mm: number; estimated: boolean };
  /** A110Z: null while the engraving is the house one. */
  engraving?: EngravingOrder | null;
}

/** Copy used by the helpers: the order block plus the shared colour words of the Colours panel. */
export interface OrderCopy {
  order: OrderWords;
  original: string;
  intensity: readonly [string, string];
  /** A110Z: engraving words (the Gravure line, the cart property names). */
  engrave?: EngraveWords;
}

/** Shopify storefront the try-on is embedded in. */
export interface ShopContext { origin: string; root: string }

export type OrderLineKey = 'model' | 'acetate' | 'lenses' | 'size' | 'pd' | 'engraving';
export interface OrderLine { key: OrderLineKey; label: string; value: string }

/** « Solace — Tortoise ». */
export function frameTitle(s: OrderSnapshot): string {
  return s.colorway ? `${s.model} — ${s.colorway}` : s.model;
}

/** « Clair » / « Foncé » for the two intensities of the Colours panel, null otherwise. */
export function intensityWord(density: number, copy: OrderCopy): string | null {
  if (density === INTENSITY_LIGHT) return copy.intensity[0];
  if (density === INTENSITY_DARK) return copy.intensity[1];
  return null;
}

/** « Jaguar (J810907-6) », « Jaguar » (no reference yet) or « Original (Tortoise) ». */
export function acetateValue(s: OrderSnapshot, copy: OrderCopy, withRef = true): string | null {
  const m = s.materials;
  if (!m || m.lensOnly) return null;
  if (m.acetateId === 'original') return s.colorway ? `${copy.original} (${s.colorway})` : copy.original;
  const name = m.acetateName ?? m.acetateId;
  return withRef && m.acetateRef ? `${name} (${m.acetateRef})` : name;
}

/**
 * Lens wording: name (or « Original »), then the intensity when it is one of
 * the two panel values, then the finish. A tinted lens says « dégradé » when
 * the gradient is on; the original lens only mentions a finish that differs
 * from the frame's own (« dégradé » / « sans dégradé »). Sunset lenses have
 * no gradient option.
 */
export function lensParts(s: OrderSnapshot, copy: OrderCopy): string[] | null {
  const m = s.materials;
  if (!m) return null;
  const original = m.lensId === 'original';
  const parts = [original ? copy.original : m.lensName ?? m.lensId];
  // A202: a Sunset lens on a Signature — its intensity and the supplement (« Sunset · Foncé · + Rp 990.000 »).
  if (m.lensOnly && isSunsetId(m.lensId)) {
    const intensity = intensityWord(m.density, copy);
    if (intensity) parts.push(intensity);
    parts.push(`+ ${formatIdr(SIGNATURE_SUNSET_SUPPLEMENT_IDR)}`);
    return parts;
  }
  // « Lunettes de vue » (clear lenses) is named alone, on Handmade too (A201): no intensity, no gradient.
  if (m.lensOnly || m.lensId === 'clear') return parts;
  const intensity = intensityWord(m.density, copy);
  if (intensity) parts.push(intensity);
  if (original) {
    if (m.gradient !== m.defaultGradient) parts.push(m.gradient ? copy.order.gradient : copy.order.noGradient);
  } else if (m.gradient) {
    parts.push(copy.order.gradient);
  }
  return parts;
}

/** « Sunset · Foncé », « Original », « Brun · Clair · dégradé ». */
export function lensValue(s: OrderSnapshot, copy: OrderCopy): string | null {
  return lensParts(s, copy)?.join(' · ') ?? null;
}

function mm(value: number): string {
  return `${Math.round(value * 10) / 10} mm`;
}

/** « M · 145 mm » (Handmade), « 142 mm » (fixed size), null when unknown. */
export function sizeValue(s: OrderSnapshot): string | null {
  const width = s.size.widthMm !== null && Number.isFinite(s.size.widthMm) && s.size.widthMm > 0 ? mm(s.size.widthMm) : null;
  if (s.size.label && width) return `${s.size.label} · ${width}`;
  return s.size.label ?? width;
}

/** « 63 mm (estimée) » or « 63 mm » (typed in by the customer). */
export function pdValue(s: OrderSnapshot, copy: OrderCopy): string {
  return `${mm(s.pd.mm)}${s.pd.estimated ? ` (${copy.order.estimated})` : ''}`;
}

/**
 * Spec list, in order; only what exists for this frame. The panel shows the acetate by name;
 * the WhatsApp message (`withRef`) adds the supplier reference for the workshop.
 */
export function orderLines(s: OrderSnapshot, copy: OrderCopy, withRef = false): OrderLine[] {
  const labels = copy.order.labels;
  const lines: OrderLine[] = [{ key: 'model', label: labels.model, value: frameTitle(s) }];
  const acetate = acetateValue(s, copy, withRef);
  if (acetate) lines.push({ key: 'acetate', label: labels.acetate, value: acetate });
  const lens = lensValue(s, copy);
  if (lens) lines.push({ key: 'lenses', label: labels.lenses, value: lens });
  const size = sizeValue(s);
  if (size) lines.push({ key: 'size', label: labels.size, value: size });
  lines.push({ key: 'pd', label: labels.pd, value: pdValue(s, copy) });
  const engraving = engravingValue(s, copy);
  if (engraving) lines.push({ key: 'engraving', label: copy.engrave?.orderLabel ?? 'Gravure', value: engraving });
  return lines;
}

/** A110Z: « Cormorant · Or · 3 textes », null while nothing was changed. */
export function engravingValue(s: OrderSnapshot, copy: OrderCopy): string | null {
  const e = s.engraving;
  if (!e) return null;
  const count = copy.engrave?.textsCount(e.texts) ?? `${e.texts}`;
  return `${e.fontName} · ${e.inkName} · ${count}`;
}

/** A110Z: one line per changed slot, « Intérieur droit : « Pour Léa » (taille L · centré) ». */
export function engravingDetailLines(s: OrderSnapshot, copy: OrderCopy): string[] {
  const e = s.engraving;
  if (!e) return [];
  const sep = copy.order.separator;
  // A201: the logo is named, not quoted (it is not a text the customer typed).
  return e.slots.map(slot => `${slot.label}${sep}${slot.slot === 'il' ? slot.text : slot.text ? `« ${slot.text} »` : copy.engrave?.emptied ?? '—'}${slot.detail ? ` (${slot.detail})` : ''}`);
}

/** Shopify routes root as a path with leading and trailing slashes (« / », « /fr/ »). */
export function normaliseRoot(root: string): string {
  let r = root || '/';
  if (!r.startsWith('/')) r = `/${r}`;
  if (!r.endsWith('/')) r = `${r}/`;
  return r;
}

/** Product page: the storefront's own page when embedded, maisonmata.com otherwise. */
export function productUrl(handle: string, shop: ShopContext | null): string | null {
  if (!handle) return null;
  const path = `products/${encodeURIComponent(handle)}`;
  return shop ? `${shop.origin}${normaliseRoot(shop.root)}${path}` : `${PUBLIC_SHOP_ROOT}${path}`;
}

/**
 * A201 (David : « PDF + code commande ») — the code that ties the WhatsApp message, the photo, the order sheet and
 * the cart line together: « MM-0910-K7Q4F » (day and month, then 5 characters from the choices and the visit).
 * The same choices in the same visit keep their code; another choice is another order.
 */
export function orderCode(s: OrderSnapshot, salt: string, date: Date = new Date()): string {
  const seed = `${salt}|${JSON.stringify(s)}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // no I, L, O, U: read aloud without doubt
  let code = '';
  for (let i = 0; i < 5; i++) { code += alphabet[h & 31]; h >>>= 5; }
  const two = (n: number) => String(n).padStart(2, '0');
  return `MM-${two(date.getDate())}${two(date.getMonth() + 1)}-${code}`;
}

/** WhatsApp message in the UI language (the customer sends it themselves). A201: the order code first. */
export function whatsappMessage(s: OrderSnapshot, copy: OrderCopy, url: string | null, code: string | null = null): string {
  const o = copy.order;
  const lines = [o.greeting];
  if (code && o.code) lines.push(`• ${o.code}${o.separator}${code}`);
  lines.push(...orderLines(s, copy, true).map(l => `• ${l.label}${o.separator}${l.value}`));
  // A110Z: the engraved texts follow the Gravure line, one per slot.
  for (const detail of engravingDetailLines(s, copy)) lines.push(`   – ${detail}`);
  if (url) lines.push(url);
  lines.push(o.photoFollows);
  return lines.join('\n');
}

export function whatsappUrl(message: string, number: string = WHATSAPP_NUMBER): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

/** Line-item properties of the cart, in the UI language. */
export function cartProperties(s: OrderSnapshot, copy: OrderCopy, code: string | null = null): Record<string, string> {
  const c = copy.order.cart;
  const props: Record<string, string> = {};
  if (code && c.code) props[c.code] = code;
  const acetate = acetateValue(s, copy);
  if (acetate) props[c.acetate] = acetate;
  const lens = lensValue(s, copy);
  if (lens) props[c.lenses] = lens;
  const size = sizeValue(s);
  if (size) props[c.size] = size;
  props[c.pd] = pdValue(s, copy);
  props[c.tryOn] = c.yes;
  // A110Z: font, colour, then each changed slot (defaults are not sent).
  const e = s.engraving;
  if (e && copy.engrave) {
    props[copy.engrave.cart.font] = e.fontName;
    props[copy.engrave.cart.ink] = e.inkName;
    for (const slot of e.slots) props[copy.engrave.cart.slot(slot.slot)] = `${slot.text || copy.engrave.emptied}${slot.detail ? ` (${slot.detail})` : ''}`;
  }
  return props;
}

export interface ShopVariant {
  id: number | string;
  title?: string;
  available?: boolean;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
}
export interface ShopProduct { variants?: ShopVariant[] }

/**
 * Variant to add: the first available one, preferring the one named after the
 * frame colourway (title, then any option value; case-insensitive).
 */
export function pickVariant(product: ShopProduct | null | undefined, colorway: string): ShopVariant['id'] | null {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const available = variants.filter(v => v && v.available !== false && (typeof v.id === 'number' || (typeof v.id === 'string' && v.id !== '')));
  const want = colorway.trim().toLowerCase();
  const same = (value: string | null | undefined) => typeof value === 'string' && value.trim().toLowerCase() === want;
  const match = want ? available.find(v => same(v.title)) ?? available.find(v => same(v.option1) || same(v.option2) || same(v.option3)) : undefined;
  return (match ?? available[0])?.id ?? null;
}

/**
 * A202: an article added with the frame (the Sunset supplement of a Signature): the store product, its variant by
 * exact title, the line properties.
 */
export interface CartExtra { handle: string; variant: string; properties: Record<string, string> }

/** `supplement`: only when extras were asked — added with the frame, or not found in the store (the frame alone). */
export type CartResult = { ok: true; variantId: ShopVariant['id']; supplement?: 'added' | 'missing' } | { ok: false; code: string };

/** The available variant titled exactly `title` (case and spaces aside), or null. */
export function exactVariant(product: ShopProduct | null | undefined, title: string): ShopVariant['id'] | null {
  const want = title.trim().toLowerCase();
  const v = (product?.variants ?? []).find(x => x.available !== false && typeof x.title === 'string' && x.title.trim().toLowerCase() === want);
  return v?.id ?? null;
}

/**
 * A202: the properties of the Sunset supplement line — which frame it goes with, the lens as chosen (no price: the
 * line carries it), the order code.
 */
export function supplementProperties(s: OrderSnapshot, copy: OrderCopy, code: string | null = null): Record<string, string> {
  const c = copy.order.cart;
  const props: Record<string, string> = {};
  if (code && c.code) props[c.code] = code;
  props[c.forFrame] = frameTitle(s);
  const parts = lensParts(s, copy);
  if (parts) props[c.lenses] = parts.filter(p => !p.startsWith('+ ')).join(' · ');
  return props;
}

/**
 * Shopify Ajax cart: reads `/products/<handle>.js`, picks the variant, then
 * posts `/cart/add.js` with the properties. Never throws; `code` names the
 * failure for analytics (no personal data).
 */
export async function addToCart(opts: {
  root: string;
  handle: string;
  colorway: string;
  properties: Record<string, string>;
  /** A202: articles added in the same request (the Sunset supplement). */
  extras?: readonly CartExtra[];
  fetch?: typeof fetch;
}): Promise<CartResult> {
  const request = opts.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const root = normaliseRoot(opts.root);
  let product: ShopProduct;
  try {
    const res = await request(`${root}products/${encodeURIComponent(opts.handle)}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) return { ok: false, code: `product_${res.status}` };
    try {
      product = (await res.json()) as ShopProduct;
    } catch {
      return { ok: false, code: 'product_json' };
    }
  } catch {
    return { ok: false, code: 'network' };
  }
  const id = pickVariant(product, opts.colorway);
  if (id === null) return { ok: false, code: 'unavailable' };
  const items: Array<{ id: ShopVariant['id']; quantity: number; properties: Record<string, string> }> = [{ id, quantity: 1, properties: opts.properties }];
  let supplement: 'added' | 'missing' | undefined;
  // Resolve every required variant before sending one atomic cart request.
  for (const extra of opts.extras ?? []) {
    let vid: ShopVariant['id'] | null = null;
    try {
      const res = await request(`${root}products/${encodeURIComponent(extra.handle)}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (res.ok) vid = exactVariant((await res.json()) as ShopProduct, extra.variant);
    } catch {
      vid = null;
    }
    if (vid === null) return { ok: false, code: 'extra_unavailable' };
    items.push({ id: vid, quantity: 1, properties: extra.properties }); supplement = 'added';
  }
  const reference = opts.properties._vto_dossier;
  const existingDossier = async (): Promise<boolean | null> => {
    try {
      const res = await request(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) return null;
      const cart = await res.json() as { items?: Array<{ variant_id: number | string; properties?: Record<string, string> }> };
      const matches = (cart.items ?? []).filter(item => item.properties?._vto_dossier === reference);
      if (!matches.length) return false;
      return items.every(expected => matches.some(actual => String(actual.variant_id) === String(expected.id))) ? true : null;
    } catch { return null; }
  };
  if (reference) {
    const exists = await existingDossier();
    if (exists === true) return supplement ? { ok: true, variantId: id, supplement } : { ok: true, variantId: id };
    if (exists === null) return { ok: false, code: 'cart_verification' };
  }
  try {
    const res = await request(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });
    if (!res.ok) return { ok: false, code: `cart_${res.status}` };
  } catch {
    if (reference && await existingDossier() === true) return supplement ? { ok: true, variantId: id, supplement } : { ok: true, variantId: id };
    return { ok: false, code: 'network' };
  }
  return supplement ? { ok: true, variantId: id, supplement } : { ok: true, variantId: id };
}

/** Download name of the photo. */
export function photoFileName(frameId: string): string {
  const safe = frameId.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'essayage';
  return `maison-mata-${safe}.jpg`;
}

/**
 * Cover crop of a `srcW × srcH` image into a `dstW × dstH` box, centred on a
 * focus point (fractions of the source) and kept inside the source.
 */
export function coverRect(srcW: number, srcH: number, dstW: number, dstH: number, focus: { x: number; y: number } = DEFAULT_PHOTO_FOCUS): { sx: number; sy: number; sw: number; sh: number } {
  if (!(srcW > 0 && srcH > 0 && dstW > 0 && dstH > 0)) return { sx: 0, sy: 0, sw: Math.max(0, srcW), sh: Math.max(0, srcH) };
  const scale = Math.max(dstW / srcW, dstH / srcH);
  const sw = Math.min(srcW, dstW / scale);
  const sh = Math.min(srcH, dstH / scale);
  const clamp = (v: number, max: number) => Math.min(Math.max(v, 0), max);
  const fx = Number.isFinite(focus.x) ? focus.x : DEFAULT_PHOTO_FOCUS.x;
  const fy = Number.isFinite(focus.y) ? focus.y : DEFAULT_PHOTO_FOCUS.y;
  return { sx: clamp(fx * srcW - sw / 2, srcW - sw), sy: clamp(fy * srcH - sh / 2, srcH - sh), sw, sh };
}

/** Texts of the photo band. */
export interface PhotoText { brand: string; title: string; specs: string[]; url: string | null }

/** « MAISON MATA », « Solace — Tortoise », « Acétate · Jaguar », « Verres · Sunset Foncé », « Taille M · 140 mm », « PD 63 mm », address without protocol. */
export function photoText(s: OrderSnapshot, copy: OrderCopy, url: string | null, code: string | null = null): PhotoText {
  const labels = copy.order.labels;
  const specs: string[] = [];
  const m = s.materials;
  if (m) {
    specs.push(`${labels.acetate} · ${m.acetateId === 'original' ? copy.original : m.acetateName ?? m.acetateId}`);
    specs.push(`${labels.lenses} · ${(lensParts(s, copy) ?? []).join(' ')}`);
  }
  const size = sizeValue(s);
  if (size) specs.push(`${labels.size} ${size}`);
  specs.push(`${labels.pd} ${pdValue(s, copy)}`);
  // A201: the order code at the end of the address line.
  const foot = [url ? url.replace(/^https?:\/\//, '') : null, code].filter(Boolean).join('   ·   ');
  return { brand: 'MAISON MATA', title: frameTitle(s), specs, url: foot || null };
}

/**
 * A110W: caption of the photo shared at step 2, in the UI language. Short on purpose — the
 * full choices are already in the WhatsApp message: model — acetate (the frame colourway for
 * « Original »), the lens when it is not the frame's own, the size letter (Handmade).
 * « Ma paire Maison Mata : Solace — Jaguar · Sunset Foncé · Taille M ».
 */
export function photoCaption(s: OrderSnapshot, copy: OrderCopy): string {
  const labels = copy.order.labels;
  const m = s.materials;
  const colour = m && m.acetateId !== 'original' ? m.acetateName ?? m.acetateId : s.colorway;
  const parts = [colour ? `${s.model} — ${colour}` : s.model];
  const lens = lensParts(s, copy);
  if (m && lens) {
    // The frame's own lens is in the photo: worded only when its intensity or finish was changed.
    if (m.lensId !== 'original') parts.push(lens.join(' '));
    else if (lens.length > 1) parts.push(`${labels.lenses} ${lens.join(' ')}`);
  }
  if (s.size.label) parts.push(`${labels.size} ${s.size.label}`);
  return `${copy.order.captionLead}${parts.join(' · ')}`;
}

/** Greedy wrap of spec items into lines no wider than `maxWidth` (an item longer than a line keeps its own line). */
export function wrapItems(items: readonly string[], maxWidth: number, measure: (text: string) => number, separator = '   |   '): string[] {
  const lines: string[] = [];
  let current = '';
  for (const item of items) {
    const next = current ? `${current}${separator}${item}` : item;
    if (!current || measure(next) <= maxWidth) current = next;
    else { lines.push(current); current = item; }
  }
  if (current) lines.push(current);
  return lines;
}

/** Baselines (px from the top of the band) for one or two spec lines. */
export function bandBaselines(specLines: number): { brand: number; title: number; specs: number[]; url: number } {
  return specLines <= 1
    ? { brand: 62, title: 122, specs: [172], url: 226 }
    : { brand: 52, title: 110, specs: [158, 194], url: 240 };
}
